import { SearchIcon } from "@chakra-ui/icons";
import { Box, BoxProps, Center, Flex, IconButton, Spinner, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useContext } from "react";
import { FormattedMessage } from "react-intl";
import Map, { LngLat, MapMouseEvent, MapProps, Popup, ViewState } from "react-map-gl/maplibre";

import { Scenario, useScenarioRaster } from "@nens/ldo/api-v1";
import { getRasterPointValue } from "@nens/ldo/api-v1";
import { LdoAuthContext } from "@nens/ldo/auth-v1";
import { RasterStyle, getObservationTypeUnit } from "@nens/lizard/api-v4";
import { boundingBoxToViewport } from "@nens/react-map-gl/utils";
import { BoundingBox } from "@nens/shared-utils";

import { RasterLayer } from "../raster-layer/raster-layer";

interface ComparisonMapProps extends BoxProps {
  mapStyle: MapProps["mapStyle"];
  viewState: ViewState;
  setViewState: (viewState: ViewState) => void;
  scenario: Scenario | null;
  rasterType: string;
  sizePx: number;
  onHover: (event: MapMouseEvent) => void;
  onMouseOut: () => void;
  currentLocation: LngLat | null;
}

export function ComparisonMap({
  mapStyle,
  viewState,
  setViewState,
  scenario,
  rasterType,
  sizePx,
  onHover,
  onMouseOut,
  currentLocation,
  ...rest
}: ComparisonMapProps) {
  const { mapLibreTransformFunction } = useContext(LdoAuthContext);
  const raster = scenario?.rasters.find((raster) => raster.type === rasterType);
  const scenarioRasterResponse = useScenarioRaster(scenario, raster ? rasterType : null);
  const scenarioRaster = scenarioRasterResponse.data ?? null;

  const rasterStyle = scenarioRaster
    ? new RasterStyle(
        scenarioRaster,
        null,
        null,
        null /* rasterMin, rasterMax, editedRasterStyle */
      )
    : null;

  const mapId = `comparison-map-${scenario?.id ?? "none"}`;

  const zoomToRaster = useCallback(() => {
    const spatialBounds = scenarioRaster?.spatial_bounds;
    const boundingBox = spatialBounds
      ? new BoundingBox(
          spatialBounds.west,
          spatialBounds.south,
          spatialBounds.east,
          spatialBounds.north
        )
      : null;
    const viewStateToZoomTo = boundingBox ? boundingBoxToViewport(mapId, boundingBox) : null;
    if (viewStateToZoomTo) setViewState(viewStateToZoomTo);
  }, [scenarioRaster, mapId, setViewState]);

  const rasterPointResponse = useQuery(
    getRasterPointValue(
      scenario?.id ?? -1,
      raster?.id ?? "",
      currentLocation?.lng ?? null,
      currentLocation?.lat ?? null
    )
  );
  const value = rasterPointResponse.isSuccess ? rasterPointResponse.data : null;
  const unit = getObservationTypeUnit(scenarioRaster?.observation_type ?? null);

  return (
    <Box
      position="relative"
      {...rest}
      borderRadius="4px"
      w={`${sizePx + 2}px`}
      h={`${sizePx + 2}px`}
      borderWidth="1px"
      borderColor="gray" /* Cloudy white */
      boxShadow="2px 2px gray"
      id={mapId}
    >
      {scenario ? (
        raster ? (
          scenarioRaster && rasterStyle ? (
            !scenarioRaster.temporal ? (
              <Map
                style={{
                  width: `${sizePx}px`,
                  height: `${sizePx}px`,
                  borderRadius: "4px",
                  overflow: "hidden",
                  padding: "0",
                }}
                id={mapId}
                mapStyle={mapStyle}
                attributionControl={false}
                viewState={{ ...viewState, width: sizePx, height: sizePx }}
                transformRequest={mapLibreTransformFunction}
                onMove={(evt) => setViewState(evt.viewState)}
                onMouseMove={onHover}
                onMouseOut={onMouseOut}
              >
                <RasterLayer raster={scenarioRaster} rasterStyle={rasterStyle} />
                {currentLocation !== null && value !== null ? (
                  <Popup
                    longitude={currentLocation.lng}
                    latitude={currentLocation.lat}
                    closeButton={false}
                  >
                    {value.toFixed(2)} {unit}
                  </Popup>
                ) : null}
              </Map>
            ) : (
              <p>
                <FormattedMessage
                  id="pages.scenario.compare.notemporal"
                  defaultMessage="Temporal rasters can not be compared."
                />
              </p>
            )
          ) : (
            <Center w="full" h="full">
              <Spinner size="xl" />
            </Center>
          )
        ) : (
          <p>
            <FormattedMessage
              id="pages.scenario.compare.nosuchraster"
              defaultMessage="This scenario has no raster of this type."
            />
          </p>
        )
      ) : (
        <Center>
          <Spinner size="xl" />
        </Center>
      )}
      <Flex position="absolute" left="10px" bottom="10px" zIndex="1000" gap="4px">
        <IconButton size="sm" onClick={zoomToRaster} aria-label="Zoom to raster">
          <SearchIcon />
        </IconButton>
        <Text p="4px" bg="white" color="black">
          {scenario?.id}
        </Text>
      </Flex>
    </Box>
  );
}
